/* Staten Tools sheave photo preview: maps flat black artwork onto the calibrated curved
   outer rim of the real product photo (photos are drawn unmodified; only marks are overlaid).
   API: window.stSheavePreview.render(root, artworkCanvas, {size, widthIn, heightIn, ready})
        window.stSheavePreview.export(root) -> Promise<{file, placement} | null>
   Calibration (photo normalized to 1000px width, y down, angles in degrees,
   0 = right, 90 = bottom, 180 = left): ellipse of the front flange face (cx, cy, rx, ry),
   band = radial fractions of that ellipse that stay on flat metal (outside ribs/spokes/hub),
   mid/span = arc centre and arc used by full max width. */
(() => {
  'use strict';
  if (window.stSheavePreview) return;
  const BASE = 'https://cdn.shopify.com/s/files/1/0849/5130/2451/files/';
  const CAL = {
    // Six-inch placement follows the supplied rim examples: lower-left through
    // bottom to lower-right, within the outer 86–99% band, tops toward the hub.
    '6':  {src: BASE + '6_inch_Cable_Puller_Sheave.jpg?v=1755896044', cx: 412, cy: 814, rx: 124, ry: 181, rotation: -15, band: [0.86, 0.99], mid: 119, span: 80, maxW: 4, maxH: 0.5},
    '12': {src: BASE + '12_1.jpg?v=1758634901', cx: 417, cy: 734, rx: 158, ry: 254, rotation: 7, band: [0.79, 0.95], mid: 140, span: 94, maxW: 8, maxH: 1},
    '18': {src: BASE + 'Mainimage2_2.jpg?v=1773679354', cx: 532, cy: 795, rx: 266, ry: 357, rotation: 5, band: [0.88, 0.965], mid: 137, span: 82, maxW: 8, maxH: 1.5},
    '24': {src: BASE + 'Mainimage2_3.jpg?v=1773679882', cx: 541, cy: 766, rx: 278, ry: 388, rotation: 8, band: [0.885, 0.965], mid: 30, span: 80, maxW: 8, maxH: 1.5}
  };
  const NOTE = 'Curved placement is illustrative. Our team confirms a suitable flat engraving surface before marking.';
  const states = new WeakMap();
  const imgCache = new Map();

  function loadImg(size) {
    if (imgCache.has(size)) return imgCache.get(size);
    const p = new Promise((res, rej) => {
      const im = new Image();
      im.crossOrigin = 'anonymous';
      im.decoding = 'async';
      im.onload = () => res(im);
      im.onerror = () => rej(Error(`The ${size} inch sheave photo could not be loaded.`));
      im.src = CAL[size].src;
    });
    p.catch(() => imgCache.delete(size));
    imgCache.set(size, p);
    return p;
  }

  function ensureUI(root) {
    let st = states.get(root);
    if (st && st.el.isConnected) return st;
    const el = document.createElement('div');
    el.className = 'st-sheave';
    el.setAttribute('data-st-sheave-preview', '');
    el.hidden = true;
    el.innerHTML = '<div class="st-sheave__head"><span class="st-sheave__title">On-tool photo preview</span><span class="st-sheave__size" data-sp-size></span></div>' +
      '<div class="st-sheave__stage" data-sp-stage><canvas class="st-sheave__canvas" role="img" aria-label="Sheave photo preview"></canvas></div>' +
      '<button class="st-sheave__expand" type="button" data-sp-expand disabled>Expand preview</button>' +
      '<p class="st-sheave__status" role="status" aria-live="polite" data-sp-status></p>' +
      '<p class="st-sheave__note">' + NOTE + '</p>';
    const host = root.querySelector('[data-eng-preview]');
    if (host) host.insertBefore(el, host.firstChild); else root.prepend(el);
    st = {el, canvas: el.querySelector('canvas'), token: 0, pending: Promise.resolve(), result: null, error: null, size: ''};
    // The theme can inject product forms before their stylesheet has applied.
    // Never let the high-resolution export bitmap set the visible element size.
    Object.assign(st.canvas.style, {display: 'block', width: '100%', height: 'auto', maxWidth: '100%', maxHeight: 'min(420px, 60vh)', objectFit: 'contain'});
    states.set(root, st);
    el.querySelector('[data-sp-expand]').addEventListener('click', async event => {
      event.preventDefault();
      event.stopPropagation();
      await st.pending;
      if (st.error || !st.result) return;
      try {
        if (!window.stEngravingExpand) throw Error('The expanded viewer is still loading. Please refresh and try again.');
        window.stEngravingExpand.open(st.canvas);
      } catch (e) { status(st, 'error', e.message || 'Unable to expand this preview.'); }
    });
    return st;
  }

  function status(st, tone, text) {
    const s = st.el.querySelector('[data-sp-status]');
    s.textContent = text; s.dataset.tone = tone;
    st.el.querySelector('[data-sp-stage]').dataset.busy = String(tone === 'loading');
    st.el.querySelector('[data-sp-expand]').disabled = tone === 'loading' || tone === 'error';
  }

  // Affine-map source triangle (s0,s1,s2) to destination triangle (d0,d1,d2), clipped.
  function tri(ctx, img, s0, s1, s2, d0, d1, d2) {
    const ex = (d0[0] + d1[0] + d2[0]) / 3, ey = (d0[1] + d1[1] + d2[1]) / 3;
    const g = p => { const dx = p[0] - ex, dy = p[1] - ey, l = Math.hypot(dx, dy) || 1; return [p[0] + dx / l * 0.6, p[1] + dy / l * 0.6]; };
    const a = g(d0), b = g(d1), c = g(d2);
    ctx.save();
    ctx.beginPath(); ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]); ctx.lineTo(c[0], c[1]); ctx.closePath(); ctx.clip();
    const sx1 = s1[0] - s0[0], sy1 = s1[1] - s0[1];
    const sx2 = s2[0] - s0[0], sy2 = s2[1] - s0[1];
    const dx1 = d1[0] - d0[0], dy1 = d1[1] - d0[1];
    const dx2 = d2[0] - d0[0], dy2 = d2[1] - d0[1];
    const den = sx1 * sy2 - sx2 * sy1;
    if (Math.abs(den) < 1e-9) { ctx.restore(); return; }
    const m11 = (dx1 * sy2 - dx2 * sy1) / den;
    const m12 = (dy1 * sy2 - dy2 * sy1) / den;
    const m21 = (dx2 * sx1 - dx1 * sx2) / den;
    const m22 = (dy2 * sx1 - dy1 * sx2) / den;
    const dx = d0[0] - m11 * s0[0] - m21 * s0[1];
    const dy = d0[1] - m12 * s0[0] - m22 * s0[1];
    ctx.transform(m11, m12, m21, m22, dx, dy);
    ctx.drawImage(img, 0, 0);
    ctx.restore();
  }

  function placement(size, w, h, mid) {
    const c = CAL[size];
    const wf = Math.min(1, w / c.maxW), hf = Math.min(1, h / c.maxH);
    const span = c.span * wf, rMid = (c.band[0] + c.band[1]) / 2, thick = (c.band[1] - c.band[0]) * hf;
    return {c, mid: mid ?? c.mid, span, rIn: rMid - thick / 2, rOut: rMid + thick / 2};
  }

  function warp(ctx, art, pl) {
    const {c, mid, span, rIn, rOut} = pl;
    const W = art.width, H = art.height, NX = Math.max(24, Math.ceil(span * 1.5)), NY = 4;
    const P = (u, v) => {
      // u=0 at the upper-left end, running down to the bottom; v=0 (artwork top) faces the hub.
      const t = (mid + (0.5 - u) * span) * Math.PI / 180, r = rIn + v * (rOut - rIn);
      const phi = c.rotation * Math.PI / 180, x = c.rx * r * Math.cos(t), y = c.ry * r * Math.sin(t);
      return [c.cx + x * Math.cos(phi) - y * Math.sin(phi), c.cy + x * Math.sin(phi) + y * Math.cos(phi)];
    };
    for (let i = 0; i < NX; i++) for (let j = 0; j < NY; j++) {
      const u0 = i / NX, u1 = (i + 1) / NX, v0 = j / NY, v1 = (j + 1) / NY;
      const sA = [u0 * W, v0 * H], sB = [u1 * W, v0 * H], sC = [u1 * W, v1 * H], sD = [u0 * W, v1 * H];
      const dA = P(u0, v0), dB = P(u1, v0), dC = P(u1, v1), dD = P(u0, v1);
      tri(ctx, art, sA, sB, sC, dA, dB, dC);
      tri(ctx, art, sA, sC, sD, dA, dC, dD);
    }
  }

  // Use more of the existing rim band instead of shrinking lettering along with
  // the empty space above/below it. Keep horizontal layout and the original flat
  // production artwork intact; cap enlargement to preserve fine logo details.
  function wheelArtwork(art) {
    const snap = document.createElement('canvas');
    snap.width = Math.min(1200, art.width);
    snap.height = Math.max(1, Math.round(art.height * snap.width / art.width));
    const sc = snap.getContext('2d', {willReadFrequently: true});
    sc.drawImage(art, 0, 0, snap.width, snap.height);
    const pixels = sc.getImageData(0, 0, snap.width, snap.height).data;
    let top = snap.height, bottom = -1;
    for (let y = 0; y < snap.height; y++) {
      for (let x = 0; x < snap.width; x++) {
        const i = (y * snap.width + x) * 4;
        if (pixels[i + 3] > 24 && pixels[i] + pixels[i + 1] + pixels[i + 2] < 690) {
          top = Math.min(top, y); bottom = y; break;
        }
      }
    }
    if (bottom < top) return snap;
    const height = Math.min(snap.height, Math.max(Math.ceil((bottom - top + 1) * 1.16), Math.ceil(snap.height / 3)));
    const y = Math.max(0, Math.min(snap.height - height, Math.floor((top + bottom + 1 - height) / 2)));
    const fitted = document.createElement('canvas');
    fitted.width = snap.width; fitted.height = height;
    fitted.getContext('2d').drawImage(snap, 0, y, snap.width, height, 0, 0, snap.width, height);
    return fitted;
  }

  async function doRender(root, st, token, art, o) {
    const size = String(o.size || '');
    st.size = size; st.result = null; st.error = null;
    if (!CAL[size]) { st.el.hidden = true; return; }
    st.el.hidden = false;
    const c = CAL[size];
    const w = Math.min(Number(o.widthIn) || c.maxW, c.maxW), h = Math.min(Number(o.heightIn) || c.maxH, c.maxH);
    st.el.querySelector('[data-sp-size]').textContent = `${size}" sheave, max ${c.maxW} x ${c.maxH} in`;
    status(st, 'loading', 'Loading product photo...');
    let img;
    try { img = await loadImg(size); }
    catch (e) { if (token === st.token) { st.error = e; status(st, 'error', e.message + ' Check your connection and try again.'); } return; }
    if (token !== st.token || !root.isConnected) return;
    const cv = st.canvas;
    cv.width = img.naturalWidth; cv.height = img.naturalHeight;
    cv.style.aspectRatio = `${cv.width} / ${cv.height}`;
    const ctx = cv.getContext('2d');
    ctx.clearRect(0, 0, cv.width, cv.height);
    ctx.drawImage(img, 0, 0);
    const ready = !!(o.ready && art && art.width && art.height);
    let pl = null;
    if (ready) {
      // Snapshot so later redraws of the flat canvas cannot alter this frame.
      const snap = wheelArtwork(art);
      const layer = document.createElement('canvas'); layer.width = cv.width; layer.height = cv.height;
      const lc = layer.getContext('2d'); lc.imageSmoothingEnabled = true; lc.imageSmoothingQuality = 'high';
      lc.scale(cv.width / 1000, cv.width / 1000);
      // Repeated marks on the same photographed face: evenly spaced at 120 degrees.
      const marks = Number(o.marks) === 3 ? 3 : 1;
      const all = [];
      for (let i = 0; i < marks; i++) {
        const p = placement(size, w, h, c.mid + i * 120);
        warp(lc, snap, p);
        all.push(p);
      }
      pl = all[0];
      pl.marks = marks; pl.angles = all.map(p => +(((p.mid % 360) + 360) % 360).toFixed(1));
      ctx.save(); ctx.globalAlpha = 0.96; ctx.globalCompositeOperation = 'multiply'; ctx.drawImage(layer, 0, 0); ctx.restore();
      const side = size === '24' ? 'right-hand' : 'lower-left';
      const multi = pl.marks === 3;
      cv.setAttribute('aria-label', multi ? `Photo of the ${size} inch sheave front face with three black marks spaced 120 degrees around the outer rim, each ${w} by ${h} inches`
        : `Photo of the ${size} inch sheave with your black marking along the ${side} outer rim, ${w} by ${h} inches`);
      status(st, 'ready', (multi ? `Three ${w}" x ${h}" marks shown 120\u00b0 apart on the front face of the ${size}" sheave. Parts of the rim may sit behind the frame in this photo.`
        : `Your ${w}" x ${h}" marking shown on the ${side} outer rim of the ${size}" sheave.`) +
        (o.back ? ' Back face marks are shown in the package diagram below; the photo shows the front face only.' : ''));
    } else {
      cv.setAttribute('aria-label', `Unmodified photo of the ${size} inch sheave`);
      status(st, 'idle', 'Unmodified product photo. Add your text or artwork above to see it on the rim.');
    }
    st.result = {size, ready, pl, w, h};
  }

  function render(root, artworkCanvas, opts = {}) {
    if (!root) return Promise.resolve();
    const st = ensureUI(root);
    const token = ++st.token;
    st.pending = doRender(root, st, token, artworkCanvas, opts).catch(e => {
      if (token === st.token) { st.error = e; status(st, 'error', 'Photo preview failed: ' + (e?.message || 'unknown error')); }
    });
    return st.pending;
  }

  async function exportPreview(root) {
    const st = states.get(root);
    if (!st) return null;
    let t;
    do { t = st.token; await st.pending; } while (t !== st.token);
    if (!CAL[st.size]) return null;
    if (st.error || !st.result?.ready) throw Error(st.error?.message || 'The sheave photo preview is not ready. Try again.');
    const blob = await new Promise((res, rej) => {
      try { st.canvas.toBlob(b => b ? res(b) : rej(Error('Could not create the sheave photo preview image.')), 'image/png'); }
      catch (e) { rej(Error('The sheave photo could not be exported (cross-origin blocked). Refresh and try again.')); }
    });
    const r = st.result, c = CAL[r.size];
    const placement = {v: 2, size: r.size, artworkShown: r.ready, widthIn: r.w, heightIn: r.h, maxWidthIn: c.maxW, maxHeightIn: c.maxH,
      photoArtworkFit: 'vertical whitespace reduced up to 3x; original flat artwork unchanged',
      location: r.size === '24' ? 'front flange outer rim, right-hand side' : 'front flange outer rim, left to bottom', illustrative: true, photo: c.src,
      marksOnPhotographedFace: r.pl?.marks || 1, arcCentersDeg: r.pl?.angles || [c.mid], photographedFace: 'front only',
      ...(r.pl ? {arcCenterDeg: c.mid, arcSpanDeg: +r.pl.span.toFixed(2), radialBand: [+r.pl.rIn.toFixed(3), +r.pl.rOut.toFixed(3)]} : {})};
    return {file: new File([blob], `sheave-${r.size}in-preview.png`, {type: 'image/png'}), placement};
  }

  window.stSheavePreview = {render, export: exportPreview, calibration: CAL, version: 1};
})();
